/**
 * A gate in front of everything that needs a signed-in user.
 *
 * WHY IT WAITS
 * ------------
 * On a reload the app knows a token exists but has not yet heard back from the
 * server about whether it is still good. Redirecting during that moment would
 * bounce a perfectly valid session to the login screen on every refresh.
 *
 * WHY IT REMEMBERS WHERE THEY WERE GOING
 * --------------------------------------
 * Someone following a link to a specific order should land on that order after
 * signing in, not on the dashboard having lost what they were doing.
 */
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../stores/auth.js';
import { Spinner } from '../components/ui/Spinner.jsx';

export function RequireAuth({ permission, children }) {
  const location = useLocation();
  const user = useAuth((s) => s.user);
  const checking = useAuth((s) => s.checking);
  const can = useAuth((s) => s.can);

  if (checking) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label="Checking your session" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  // Signed in, but not for this. Sending them to the login screen would be
  // wrong and confusing — they are already signed in.
  if (permission && !can(permission)) {
    return (
      <main className="flex min-h-dvh items-center justify-center p-6 text-center">
        <div>
          <h1 className="font-semibold text-xl text-ink">You do not have access to this</h1>
          <p className="mx-auto mt-2 max-w-sm text-sm text-ink-2">
            Ask a manager to give your account the right permission, then sign out and in again.
          </p>
        </div>
      </main>
    );
  }

  return children;
}
